import React from "react";
import {
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from "victory";

import {
  createTooltipLabel,
  generateTooltipTimeLabel,
} from "~/utils/heartRateFormatters";

interface HeartRateData {
  date: string;
  low: number;
  high: number;
  avg: number;
}

interface HourlyHeartRateData {
  datetime: string;
  hour: number;
  low: number;
  high: number;
  avg: number;
}

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface HeartRateChartProps {
  selectedRange: TimeRange;
  navigationOffset: number;
  filteredData: (HeartRateData | HourlyHeartRateData)[];
  lowChartData: ChartDataPoint[];
  highChartData: ChartDataPoint[];
  loading: boolean;
  error: string | null;
  chartConfig: {
    scale: { x: "time" | "linear" };
    domain: { x: [Date, Date] | [number, number]; y: [number, number] };
    tickValues: (Date | number)[];
    tickFormat?: (x: Date | number) => string;
    yTickValues?: number[];
  };
}

export const HeartRateChart: React.FC<HeartRateChartProps> = ({
  selectedRange,
  navigationOffset,
  filteredData,
  lowChartData,
  highChartData,
  loading,
  error,
  chartConfig,
}) => {
  // Handle tickFormat logic internally
  const getTickFormat = (x: Date | number) => {
    if (typeof x === "number") return String(x);
    if (x instanceof Date) {
      // For year view, show month names, for other views show weekdays
      if (selectedRange === "1y" || selectedRange === "6m") {
        return x.toLocaleDateString("en-US", { month: "short" });
      } else {
        // Use local date formatting instead of UTC
        return x.toLocaleDateString("en-US", { weekday: "short" });
      }
    }
    return String(x);
  };

  const finalChartConfig = {
    ...chartConfig,
    tickFormat: chartConfig.tickFormat ?? getTickFormat,
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-sm text-gray-400">Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-sm text-red-400">Failed to load chart data</div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Chart with data */}
      {filteredData.length > 0 && (
        <VictoryChart
          key={`chart-with-data-${selectedRange}`}
          theme={VictoryTheme.material}
          width={400}
          height={200}
          style={{ parent: { maxWidth: "100%" } }}
          padding={{ left: 35, right: 35, top: 10, bottom: 30 }}
          domain={finalChartConfig.domain}
          scale={finalChartConfig.scale}
        >
          {/* Y Axis */}
          <VictoryAxis
            dependentAxis
            tickValues={chartConfig.yTickValues ?? [40, 80, 120, 160, 200]}
            style={{
              grid: {
                stroke: "rgba(107, 114, 128, 0.3)",
                strokeWidth: 1,
              },
              axis: { stroke: "none" },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "none" },
            }}
          />

          {/* X Axis */}
          <VictoryAxis
            tickValues={chartConfig.tickValues}
            tickFormat={chartConfig.tickFormat}
            style={{
              grid: { stroke: "none" },
              axis: { stroke: "rgba(107, 114, 128, 0.3)", strokeWidth: 1 },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "rgba(107, 114, 128, 0.3)", size: 5 },
            }}
            fixLabelOverlap={false}
          />

          {/* Low Heart Rate Line */}
          <VictoryLine
            data={lowChartData}
            style={{
              data: {
                stroke: "#3B82F6",
                strokeWidth: 1,
                strokeLinecap: "round",
              },
            }}
            animate={{
              duration: 300,
              easing: "sinIn",
              onLoad: { duration: 400 },
              delay: -10,
            }}
          />

          {/* High Heart Rate Line */}
          <VictoryLine
            data={highChartData}
            style={{
              data: {
                stroke: "#EF4444",
                strokeWidth: 1,
                strokeLinecap: "round",
              },
            }}
            animate={{
              duration: 300,
              onLoad: { duration: 400 },
              delay: -10,
              easing: "sinIn",
            }}
          />

          {/* Low Heart Rate Points */}
          <VictoryScatter
            data={lowChartData.map((point, index) => {
              const timeLabel = generateTooltipTimeLabel(
                selectedRange,
                index,
                filteredData,
                navigationOffset,
              );
              const highValue = highChartData[index]?.y || point.y;

              return {
                ...point,
                label: createTooltipLabel(
                  timeLabel,
                  point.y,
                  highValue,
                  index,
                  filteredData,
                ),
              };
            })}
            size={1.5}
            style={{
              data: {
                fill: "#3B82F6",
                stroke: "#1F2937",
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryTooltip
                style={{
                  fill: "#FFFFFF",
                  fontSize: 5,
                  fontFamily: "inherit",
                }}
                flyoutStyle={{
                  fill: "#1F2937",
                  stroke: "#3B82F6",
                  strokeWidth: 0.5,
                }}
                renderInPortal={true}
                pointerLength={8}
                cornerRadius={4}
              />
            }
          />

          {/* High Heart Rate Points */}
          <VictoryScatter
            data={highChartData.map((point, index) => {
              const timeLabel = generateTooltipTimeLabel(
                selectedRange,
                index,
                filteredData,
                navigationOffset,
              );
              const lowValue = lowChartData[index]?.y || point.y;

              return {
                ...point,
                label: createTooltipLabel(
                  timeLabel,
                  lowValue,
                  point.y,
                  index,
                  filteredData,
                  true,
                ),
              };
            })}
            size={1.5}
            style={{
              data: {
                fill: "#EF4444",
                stroke: "#1F2937",
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryTooltip
                style={{
                  fill: "#FFFFFF",
                  fontSize: 5,
                  fontFamily: "inherit",
                }}
                flyoutStyle={{
                  fill: "#1F2937",
                  stroke: "#EF4444",
                  strokeWidth: 0.5,
                }}
                renderInPortal={true}
                pointerLength={8}
                cornerRadius={4}
              />
            }
          />
        </VictoryChart>
      )}

      {/* Chart without data - only axes */}
      {filteredData.length === 0 && (
        <VictoryChart
          key={`chart-no-data-${selectedRange}`}
          theme={VictoryTheme.material}
          width={400}
          height={200}
          style={{ parent: { maxWidth: "100%" } }}
          padding={{ left: 35, right: 35, top: 10, bottom: 30 }}
          domain={chartConfig.domain}
          scale={chartConfig.scale}
        >
          {/* Y Axis */}
          <VictoryAxis
            dependentAxis
            tickValues={[40, 70, 100, 130, 160, 190, 220]}
            style={{
              grid: {
                stroke: "rgba(107, 114, 128, 0.3)",
                strokeWidth: 1,
              },
              axis: { stroke: "none" },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "none" },
            }}
          />

          {/* X Axis */}
          <VictoryAxis
            tickValues={chartConfig.tickValues}
            tickFormat={chartConfig.tickFormat}
            style={{
              grid: { stroke: "none" },
              axis: { stroke: "rgba(107, 114, 128, 0.3)", strokeWidth: 1 },
              tickLabels: {
                fill: "#9CA3AF",
                fontSize: 6,
                fontFamily: "inherit",
              },
              ticks: { stroke: "rgba(107, 114, 128, 0.3)", size: 5 },
            }}
            fixLabelOverlap={false}
          />
        </VictoryChart>
      )}

      {/* No Data Overlay */}
      {filteredData.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="mb-2 text-sm text-gray-500">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto mb-2"
              >
                <path
                  d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V11H13V7H7V19H17Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="text-xs text-gray-400">No data available</div>
            <div className="text-xs text-gray-500">for selected time range</div>
          </div>
        </div>
      )}
    </div>
  );
};
